export const PaymentFieldArray = ["Cash", "GST", "Adjustment"];

export const getUniqueColors = (data) => {
  const colorGroups = {};
  data.forEach((item) => {
    const color = item.cap_material.color;
    if (!colorGroups[color]) {
      colorGroups[color] = {
        color,
        material_types: {},
      };
    }

    const materialType = item.cap_material.material_type;
    if (!colorGroups[color].material_types[materialType]) {
      colorGroups[color].material_types[materialType] = [];
    }

    colorGroups[color].material_types[materialType].push(item);
  });

  // For each color group, further organize by material_type
  for (const colorGroup of Object.values(colorGroups)) {
    for (const [materialType, items] of Object.entries(
      colorGroup.material_types
    )) {
      if (materialType === "CLOTH MATERIAL") {
        // Separate by cap_side_type and cloth_type
        const clothGroups = {};
        for (const item of items) {
          const capSideType = item.cap_side_type;
          const clothType = item.cap_material.cloth_type;
          if (!clothGroups[capSideType]) {
            clothGroups[capSideType] = {};
          }
          if (!clothGroups[capSideType][clothType]) {
            clothGroups[capSideType][clothType] = [];
          }
          clothGroups[capSideType][clothType].push(item);
        }
        colorGroup.material_types[materialType] = clothGroups;
      } else if (materialType === "CAP MATERIAL") {
        // Separate by cap_material
        const capMaterialGroups = {};
        for (const item of items) {
          const capMaterialId = item.cap_material.cap_material;
          if (!capMaterialGroups[capMaterialId]) {
            capMaterialGroups[capMaterialId] = [];
          }
          capMaterialGroups[capMaterialId].push(item);
        }
        colorGroup.material_types[materialType] = capMaterialGroups;
      }
    }
  }

  return colorGroups;
};

export const getManufatureDataSet = (data) => {
  const updatedData = data.map((item) => {
    const manufacture_quantity = item.manufacture_cap.manufacture_quantity;
    const updatedManufacturedData = item.manufacture_cap.manufacturedcapdata_set.map(
      (capData) => {
        return {
          ...capData,
          // cap_current_man_quantity: parseFloat((capData.cap_current_man_quantity * manufacture_quantity).toFixed(3))
          cap_current_man_quantity: parseFloat(manufacture_quantity),
        };
      }
    );
    return {
      ...item,
      manufacture_cap: {
        ...item.manufacture_cap,
        manufacturedcapdata_set: updatedManufacturedData,
      },
    };
  });

  // Extract all manufacturedcapdata_set arrays
  const allManufacturedData = updatedData
    .map((item) => item.manufacture_cap.manufacturedcapdata_set)
    .flat();

  // Print the resulting array
  return allManufacturedData;
};

export const getSum = (caps) => {
  const getSumOfQuantity = (caps) => {
    let sum = 0;
    caps.forEach((cap) => {
      sum += cap.cap_current_man_quantity;
    });
    return sum;
  };

  const sumOfQuantity = getSumOfQuantity(caps);

  return sumOfQuantity;
};

export const sumData = (numbers) => {
  const sum = numbers?.reduce((acc, curr) => acc + curr, 0);
  return sum;
};

export const getUniqueColors2 = (data) => {
  const uniqueColors = new Set();
  if (!data || !Array.isArray(data)) return [];
  data.forEach((item) => {
    if (item?.main_fabric_Front_color) uniqueColors.add(item.main_fabric_Front_color);
    if (item?.main_fabric_Back_color) uniqueColors.add(item.main_fabric_Back_color);
    if (item?.main_fabric_Middle_color) uniqueColors.add(item.main_fabric_Middle_color);
    if (item?.main_fabric_Top_color) uniqueColors.add(item.main_fabric_Top_color);
    if (item?.main_fabric_Under_color) uniqueColors.add(item.main_fabric_Under_color);
  });
  return Array.from(uniqueColors).filter(color => color != null && color !== "");
};

// Fabric types definition used for hat quantity breakdown
const fabric = [
  { color: "main_fabric_Front_color", type: "Front" },
  { color: "main_fabric_Back_color", type: "Back" },
  { color: "main_fabric_Middle_color", type: "Middle" },
  { color: "main_fabric_Top_color", type: "Top" },
  { color: "main_fabric_Under_color", type: "Under" },
];

// Helper function to normalize eylet value (check if it's "none")
const isEyletNone = (value) => {
  if (!value) return true; // null, undefined, empty string treated as "none"
  return String(value).trim().toLowerCase() === "none";
};

// Helper function to get eylet status key for grouping
// Returns a string like "front:none,back:present" or "all:present"
const getEyletStatusKey = (record) => {
  const eyeletFront = record?.eyelet_front;
  const eyeletBack = record?.eyelet_back;
  const eyeletMiddle = record?.eyelet_middle;

  const frontIsNone = isEyletNone(eyeletFront);
  const backIsNone = isEyletNone(eyeletBack);
  const middleIsNone = isEyletNone(eyeletMiddle);

  // If all are not none, return "all:present"
  if (!frontIsNone && !backIsNone && !middleIsNone) {
    return "all:present";
  }

  // Build key for which sides are none
  const parts = [];
  if (frontIsNone) parts.push("front:none");
  if (backIsNone) parts.push("back:none");
  if (middleIsNone) parts.push("middle:none");

  return parts.join(",");
};

// Helper function to build eylet suffix for display title
const getEyletSuffix = (eyletStatusKey) => {
  if (eyletStatusKey === "all:present") {
    return ""; // No suffix if all eylets are present
  }

  // Build display suffix like "eylet front:none" or "eylet front:none,back:none"
  const parts = eyletStatusKey.split(",");
  const eyletParts = parts.map((part) => {
    const [side, status] = part.split(":");
    return `eylet ${side}:${status}`;
  });

  return " " + eyletParts.join(" ");
};

// Helper function to group records by style
const groupRecordsByStyle = (records = []) => {
  if (!Array.isArray(records) || records.length === 0) {
    return [];
  }

  const styleMap = new Map();

  records.forEach((record) => {
    if (!record) return;
    const styleName = (record.style || "Unknown Style").trim() || "Unknown Style";
    if (!styleMap.has(styleName)) {
      styleMap.set(styleName, []);
    }
    styleMap.get(styleName).push(record);
  });

  return Array.from(styleMap.entries())
    .sort(([styleA], [styleB]) => styleA.localeCompare(styleB))
    .map(([style, groupedRecords]) => ({
      style,
      records: groupedRecords,
    }));
};

// Format data into the JSON structure:
// {
//   styles: [
//     {
//       style: "6 part base ball cap velcro eylet front:none",
//       colors: [
//         {
//           color: "red",
//           total: 150,
//           parts: [{ name: "Front", quantity: 50 }, ...]
//         }
//       ]
//     }
//   ]
// }
export const formatHatDataToJSON = (recordBig) => {
  if (!Array.isArray(recordBig) || recordBig.length === 0) {
    return { styles: [] };
  }

  // Group records by style
  const styleGroups = groupRecordsByStyle(recordBig);

  const allStyles = [];

  styleGroups.forEach(({ style, records }) => {
    // Within each style, group by back_close_type
    const backClosureMap = new Map();

    records.forEach((record) => {
      const backCloseType = (record?.back_close_type || "").trim() || "Unknown";
      if (!backClosureMap.has(backCloseType)) {
        backClosureMap.set(backCloseType, []);
      }
      backClosureMap.get(backCloseType).push(record);
    });

    // For each back closure type, group by eylet status
    backClosureMap.forEach((backClosureRecords, backCloseType) => {
      const eyletStatusMap = new Map();

      backClosureRecords.forEach((record) => {
        const eyletStatusKey = getEyletStatusKey(record);
        if (!eyletStatusMap.has(eyletStatusKey)) {
          eyletStatusMap.set(eyletStatusKey, []);
        }
        eyletStatusMap.get(eyletStatusKey).push(record);
      });

      // For each eylet status group, create a style entry
      eyletStatusMap.forEach((eyletRecords, eyletStatusKey) => {
        // Build the full style title
        const eyletSuffix = getEyletSuffix(eyletStatusKey);
        const fullStyleName = `${style} ${backCloseType}${eyletSuffix}`.trim();

        // Get unique colors from these records
        const uniqueColors = getUniqueColors2(eyletRecords);

        // Create color objects
        const colors = uniqueColors
          .filter((color) => color != null && color !== "")
          .map((color) => {
            const colorData = {
              color: color,
              total: 0,
              parts: [],
            };

            // Check each fabric type (Front, Back, Middle, Top, Under)
            fabric.forEach((fabricType) => {
              const filteredItems = eyletRecords.filter(
                (item) => item && item[fabricType.color] === color
              );

              const quantities = filteredItems
                .map((it) => (it?.quantity ? Number(it.quantity) : 0))
                .filter((qty) => qty > 0);

              const totalQuantity = sumData(quantities);

              if (totalQuantity > 0) {
                colorData.parts.push({
                  name: fabricType.type,
                  quantity: totalQuantity,
                });
                colorData.total += totalQuantity;
              }
            });

            return colorData;
          })
          .filter((colorData) => colorData.parts.length > 0); // Only include colors with parts

        if (colors.length > 0) {
          allStyles.push({
            style: fullStyleName,
            colors: colors,
          });
        }
      });
    });
  });

  // Sort styles alphabetically
  allStyles.sort((a, b) => a.style.localeCompare(b.style));

  return { styles: allStyles };
};

// Helper to download the formatted JSON as a file
export const downloadAsJSON = (data, filename = "hat-inventory.json") => {
  const jsonData = formatHatDataToJSON(data);
  const dataStr = JSON.stringify(jsonData, null, 2);
  const dataBlob = new Blob([dataStr], { type: "application/json" });
  const url = URL.createObjectURL(dataBlob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
